import Box from '@mui/material/Box';
import {
  ChangeEvent,
  RefObject,
  useCallback,
  useMemo,
  FormEvent,
  useState,
  useRef,
  useEffect,
  HTMLInputTypeAttribute,
} from 'react';

import { DashPinItem } from './dash-pin-item';
import { PinItem } from './pin-item';
import { CodeValidator } from '../common/validation/code-validator';

export const DEFAULT_PIN_SIZE = 4;

export type PinFieldProps = {
  size?: number;
  pinItemType?: 'circle' | 'dash';
  cleanOnSubmit?: boolean;
  autoSubmit?: boolean;
  inputType?: HTMLInputTypeAttribute;
  blurOnSubmitEnabled?: boolean;
  onSubmit(value: string): void;
  onFocus?(): void;
  onBlur?(): void;
};

export const PinField = (props: PinFieldProps) => {
  const {
    size = DEFAULT_PIN_SIZE,
    pinItemType = 'circle',
    cleanOnSubmit = false,
    autoSubmit = false,
    inputType = 'password',
    blurOnSubmitEnabled = false,
    onFocus,
    onBlur,
    onSubmit,
  } = props;
  const submitValidator = useMemo(() => CodeValidator.exactLength(size), [size]);
  const changeValidator = useMemo(() => CodeValidator.maxLenght(size), [size]);

  const [value, setValue] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const pinItems = useMemo<RefObject<HTMLInputElement>[]>(() => Array(size).fill(null), [size]);

  const handleFocus = useCallback(() => {
    input.current?.focus();
  }, []);

  // Safari does not support auto focus
  useEffect(handleFocus, [handleFocus]);

  const cleanInput = useCallback(() => {
    setValue('');
  }, []);

  const handleSubmit = useCallback(
    (e?: FormEvent, _value?: string) => {
      e?.preventDefault();
      const valueToSubmit = _value ?? value;

      if (!submitValidator.validate(valueToSubmit).success) {
        return;
      }

      onSubmit(valueToSubmit);
      if (cleanOnSubmit) {
        cleanInput();
      }
      if (blurOnSubmitEnabled) {
        input.current?.blur();
      }
    },
    [blurOnSubmitEnabled, cleanInput, cleanOnSubmit, onSubmit, submitValidator, value],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (!changeValidator.validate(value, { allowEmpty: true }).success) {
        return;
      }

      setValue(value);
      if (autoSubmit) {
        handleSubmit(undefined, value);
      }
    },
    [autoSubmit, changeValidator, handleSubmit],
  );

  const PinItemComponent = useMemo(() => (pinItemType === 'circle' ? PinItem : DashPinItem), [pinItemType]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
    <form onClick={handleFocus} onSubmit={handleSubmit} onFocus={onFocus} onBlur={onBlur}>
      <Box
        component="input"
        // Prop for input only to handle OTP autocomplete
        autoComplete="one-time-code"
        type={inputType}
        inputMode="numeric"
        pattern="\\d*"
        ref={input}
        value={value}
        onChange={handleChange}
        sx={{
          height: 0,
          opacity: 0,
          padding: 0,
          margin: 0,
          top: 0,
          left: 0,
          border: 'none',
          position: 'absolute',
        }}
      />
      <Box display="flex" justifyContent="center">
        {pinItems.map((ref, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <PinItemComponent key={index} value={value.charAt(index)} />
        ))}
      </Box>
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      <button type="submit" hidden />
    </form>
  );
};
